import React from 'react'
import { useApp } from '../context/AppContext'
import { ApiError, EmailInUseError, ValidationError, VersionMismatchError, api } from '../api/client'
import type { ClientDetail, Consent, Note, PaymentRecord, PreferenceInput, Purchase, TimelineEntry } from '../types/app'
import { displayName, isCurrentRequest } from '../clients/list'
import ConsentRow from '../clients/ConsentRow'
import NotePhotoGrid from '../notes/NotePhotoGrid'
import { displayPhone, telHref, whatsappNumber } from '../clients/phone'
import { paymentStateLabel } from '../payments/status'
import { disputeTag } from '../payments/history'
import { importantFirst } from '../components/AppointmentSheet'
import { BIRTHDAY_MONTHS, BirthdayDraft, formatBirthday, fromBirthdayDraft, toBirthdayDraft } from '../clients/birthday'
import { todayIso } from '../calendar/dates'
import { formatDay, formatPrice, formatLength } from '../catalog/format'
import { expiryState, hasVisibleExpiry, remainingLabel } from '../packages/display'
import SellPackageSheet from '../components/SellPackageSheet'
import RefundSheet from '../components/RefundSheet'
import PaymentHistory from '../components/PaymentHistory'
import VisitPrice from '../components/VisitPrice'
import ClientReviews from './ClientReviews'
import ClientForms from './ClientForms'
import { useCardAccount } from '../payments/useCardAccount'
import { canTakeCardPayments, cardPaymentsShown } from '../payments/visibility'
import { cardExpiry, cardLabel } from '../payments/savedCard'
import { refundsToCard } from '../payments/refund'
import RemoveCardSheet from '../components/RemoveCardSheet'
import { saveErrorMessage, showToast } from './settings/toast'
import { downloadFile } from '../api/download'
import './settings/Settings.css'
import './Clients.css'
import './ClientProfile.css'
import { BackLink } from '../ui/Icon'
import { useBackStep } from '../hooks/backStep'
import { Loading } from '../ui/States'

const STATUS_LABELS: Record<TimelineEntry['status'], string> = {
  booked: 'Booked',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No-show',
}

// asDay turns a full timestamp ("2026-09-16T10:00:00Z") into the date-only
// string formatDay expects, without adding a second formatter for the same job.
function asDay(isoDateTime: string): string {
  return formatDay(isoDateTime.slice(0, 10))
}

// validationMessage joins a validation error's field messages into one line,
// since this screen doesn't highlight individual fields for these forms.
function validationMessage(error: ValidationError): string {
  return Object.values(error.fields).join(' ') || 'Check what you entered and try again.'
}

interface PrefDraft {
  key: string
  label: string
  value: string
  is_health_flag: boolean
}

let nextDraftKey = 0

function toDraft(p: { label: string; value: string; is_health_flag: boolean }): PrefDraft {
  return { key: `pref-${nextDraftKey++}`, ...p }
}

type ProfileTab = 'overview' | 'history' | 'details'
const PROFILE_TABS: { id: ProfileTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'history', label: 'History' },
  { id: 'details', label: 'Details' },
]

// tabAfterKey moves between the profile tabs with the arrow keys, Home and
// End (the ARIA tabs pattern); null for any other key.
export function tabAfterKey(current: ProfileTab, key: string): ProfileTab | null {
  const ids = PROFILE_TABS.map((t) => t.id)
  const at = ids.indexOf(current)
  switch (key) {
    case 'ArrowRight':
      return ids[(at + 1) % ids.length]
    case 'ArrowLeft':
      return ids[(at - 1 + ids.length) % ids.length]
    case 'Home':
      return ids[0]
    case 'End':
      return ids[ids.length - 1]
    default:
      return null
  }
}

// owedOn totals what the visits listed still have to pay.
export function owedOn(entries: TimelineEntry[] | null): number {
  return (entries ?? []).reduce((sum, e) => sum + (e.payment_state === 'to_pay' ? e.amount_due_pence ?? 0 : 0), 0)
}

export default function ClientProfile() {
  const { state, dispatch } = useApp()
  const clientId = state.openClientId as string
  const businessName = state.me?.business?.trading_name ?? 'us'

  const [client, setClient] = React.useState<ClientDetail | null>(null)
  const [loadFailed, setLoadFailed] = React.useState(false)
  const [vipSaving, setVipSaving] = React.useState(false)
  const clientToken = React.useRef(0)
  const aggregateToken = React.useRef(0)

  const applyClient = React.useCallback((c: ClientDetail) => {
    setClient(c)
    setPrefRows(c.preferences.map(toDraft))
  }, [])

  const loadClient = React.useCallback(() => {
    const token = ++clientToken.current
    setClient(null)
    setLoadFailed(false)
    api
      .getClient(clientId)
      .then((c) => {
        if (isCurrentRequest(token, clientToken.current)) applyClient(c)
      })
      .catch(() => {
        if (isCurrentRequest(token, clientToken.current)) setLoadFailed(true)
      })
  }, [clientId, applyClient])

  React.useEffect(() => {
    loadClient()
  }, [loadClient])

  // reloadAggregate refreshes just the client's server-computed totals (e.g.
  // total_spent_pence) after a mutation that changes them elsewhere, such as
  // a package sale, without blanking the rest of the already-loaded profile.
  // It uses its own token rather than clientToken's, so an in-flight full
  // load's result is never discarded by (or discards) an aggregate refresh.
  const reloadAggregate = React.useCallback(() => {
    const token = ++aggregateToken.current
    api
      .getClient(clientId)
      .then((c) => {
        if (isCurrentRequest(token, aggregateToken.current)) {
          setClient((prev) => (prev ? { ...prev, total_spent_pence: c.total_spent_pence } : prev))
        }
      })
      .catch(() => {
        // Best-effort refresh: leave the previous total in place rather than
        // surfacing an error for a display-only aggregate.
      })
  }, [clientId])

  const back = () => dispatch({ type: 'CLOSE_CLIENT' })
  // The phone's back gesture goes back to Clients, like the link does.
  useBackStep(back)

  const toggleVip = async () => {
    if (!client || vipSaving) return
    setVipSaving(true)
    try {
      const updated = await api.updateClient(clientId, { vip: !client.vip }, client.version)
      setClient((prev) => (prev ? { ...prev, vip: updated.vip, check_details: updated.check_details, version: updated.version } : prev))
    } catch (e) {
      showToast(dispatch, saveErrorMessage(e))
    } finally {
      setVipSaving(false)
    }
  }

  // Contact details she can edit herself (feedback 2026-10-11): an email for
  // deposit links, and a birthday for Today's reminders. The phone stays as
  // it is here, since online bookings match clients by it.
  const [editingDetails, setEditingDetails] = React.useState(false)
  const [emailDraft, setEmailDraft] = React.useState('')
  const [birthdayDraft, setBirthdayDraft] = React.useState<BirthdayDraft>({ day: '', month: '', year: '' })
  const [detailErrors, setDetailErrors] = React.useState<Record<string, string>>({})
  const [detailsSaving, setDetailsSaving] = React.useState(false)
  const startEditDetails = () => {
    if (!client) return
    setEmailDraft(client.email ?? '')
    setBirthdayDraft(toBirthdayDraft(client.birthday))
    setDetailErrors({})
    setEditingDetails(true)
  }
  const saveDetails = async () => {
    if (!client) return
    const birthday = fromBirthdayDraft(birthdayDraft)
    if ('error' in birthday) {
      setDetailErrors({ birthday: birthday.error })
      return
    }
    setDetailsSaving(true)
    setDetailErrors({})
    try {
      // The email goes only when she changed it, and never when it is
      // confirmed: a PATCH carrying a changed confirmed email saves nothing.
      const typed = emailDraft.trim()
      const emailChanged = !client.email_confirmed && typed.toLowerCase() !== (client.email ?? '').toLowerCase()
      const updated = await api.updateClient(clientId, {
        ...(emailChanged ? { email: typed || null } : {}),
        birthday: birthday.value,
      }, client.version)
      setClient((prev) => (prev ? {
        ...prev,
        email: updated.email,
        email_confirmed: updated.email_confirmed,
        possible_duplicate: updated.possible_duplicate,
        birthday: updated.birthday,
        check_details: updated.check_details,
        version: updated.version,
      } : prev))
      if (emailChanged) setConfirmationNote('')
      setEditingDetails(false)
      showToast(dispatch, 'Details saved')
    } catch (e) {
      if (e instanceof ValidationError) setDetailErrors(e.fields)
      else if (e instanceof EmailInUseError) setDetailErrors({ email: saveErrorMessage(e) })
      else showToast(dispatch, saveErrorMessage(e))
    } finally {
      setDetailsSaving(false)
    }
  }

  const confirmDetails = async () => {
    try {
      const updated = await api.clientDetailsChecked(clientId)
      setClient((prev) => (prev ? { ...prev, check_details: updated.check_details, possible_duplicate: updated.possible_duplicate ?? null } : prev))
    } catch (e) {
      showToast(dispatch, saveErrorMessage(e))
    }
  }

  // "Send confirmation": emails the client a link that confirms their email.
  const [confirmationSending, setConfirmationSending] = React.useState(false)
  const [confirmationNote, setConfirmationNote] = React.useState('')
  const [confirmationError, setConfirmationError] = React.useState('')
  const sendConfirmation = async () => {
    if (!client?.email || confirmationSending) return
    setConfirmationSending(true)
    setConfirmationNote('')
    setConfirmationError('')
    try {
      await api.resendClientConfirmation(clientId)
      setConfirmationNote(`Confirmation sent to ${client.email}.`)
    } catch (e) {
      if (e instanceof ApiError && e.problem === 'too-many') {
        setConfirmationError('Too many confirmation emails today. Try again tomorrow.')
      } else if (e instanceof ApiError && e.problem === 'email-confirmed') {
        setClient((prev) => (prev ? { ...prev, email_confirmed: true } : prev))
      } else {
        setConfirmationError(saveErrorMessage(e))
      }
    } finally {
      setConfirmationSending(false)
    }
  }

  // Quick actions
  const openBooking = () => {
    if (!client) return
    dispatch({
      type: 'OPEN_BOOK_SHEET',
      payload: {
        // Today, not state.calendarDate — the professional didn't pick a
        // date here, and defaulting to whatever day the Calendar tab last
        // happened to show (which could be closed, or long past) silently
        // books into the wrong day.
        date: todayIso(),
        startMinute: null,
        client: { id: client.id, first_name: client.first_name, last_name: client.last_name, phone: client.phone },
      },
    })
  }

  // --- Notes -------------------------------------------------------------

  const [notes, setNotes] = React.useState<Note[] | null>(null)
  const [notesFailed, setNotesFailed] = React.useState(false)
  const [addingNote, setAddingNote] = React.useState(false)
  const [newNoteText, setNewNoteText] = React.useState('')
  const [savingNewNote, setSavingNewNote] = React.useState(false)
  const [newNoteError, setNewNoteError] = React.useState('')
  const newNoteRef = React.useRef<HTMLTextAreaElement>(null)
  const notesSectionRef = React.useRef<HTMLElement>(null)
  const [tab, setTab] = React.useState<ProfileTab>(state.openClientTab ?? 'overview')
  // A notification can ask for a tab while this client is already open, so
  // the request is taken whenever it arrives, not only on mount; then it is
  // cleared, so the next visit starts on Overview.
  const askedTab = state.openClientTab
  React.useEffect(() => {
    if (!askedTab) return
    setTab(askedTab)
    dispatch({ type: 'CLIENT_TAB_SHOWN' })
  }, [askedTab, dispatch])

  const [editingNoteId, setEditingNoteId] = React.useState<string | null>(null)
  const [editText, setEditText] = React.useState('')
  const [savingEdit, setSavingEdit] = React.useState(false)
  const [editError, setEditError] = React.useState('')

  const loadNotes = React.useCallback(() => {
    setNotes(null)
    setNotesFailed(false)
    api.listNotes(clientId).then(setNotes).catch(() => setNotesFailed(true))
  }, [clientId])

  React.useEffect(() => {
    loadNotes()
  }, [loadNotes])

  React.useEffect(() => {
    if (addingNote) {
      notesSectionRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
      newNoteRef.current?.focus()
    }
  }, [addingNote])

  const startAddNote = () => {
    setTab('overview')
    setNewNoteText('')
    setNewNoteError('')
    setAddingNote(true)
  }

  const saveNewNote = async () => {
    setSavingNewNote(true)
    setNewNoteError('')
    try {
      const note = await api.createNote(clientId, { text: newNoteText })
      setNotes((prev) => [note, ...(prev ?? [])])
      setAddingNote(false)
      setNewNoteText('')
    } catch (e) {
      setNewNoteError(e instanceof ValidationError ? validationMessage(e) : saveErrorMessage(e))
    } finally {
      setSavingNewNote(false)
    }
  }

  const startEditNote = (note: Note) => {
    setEditingNoteId(note.id)
    setEditText(note.text)
    setEditError('')
  }

  // photoRemoved drops a photo from its note once the API has deleted it.
  const photoRemoved = (noteId: string, photoId: string) =>
    setNotes((prev) =>
      prev ? prev.map((n) => (n.id === noteId ? { ...n, photos: (n.photos ?? []).filter((p) => p.id !== photoId) } : n)) : prev,
    )

  const saveEditNote = async (note: Note) => {
    setSavingEdit(true)
    setEditError('')
    try {
      const updated = await api.updateNote(clientId, note.id, note.version !== undefined ? String(note.version) : note.updated_at, { text: editText })
      setNotes((prev) => (prev ? prev.map((n) => (n.id === updated.id ? updated : n)) : prev))
      setEditingNoteId(null)
    } catch (e) {
      if (e instanceof VersionMismatchError) {
        // Stay in edit mode so the message actually renders (it only shows
        // inside the editing branch) and the user's unsaved text stays on
        // screen. Reloading refreshes the note's ETag underneath, so a
        // second Save retries against the current version instead of
        // failing the same way again.
        setEditError('This note changed on another device.')
        loadNotes()
      } else if (e instanceof ValidationError) {
        setEditError(validationMessage(e))
      } else {
        setEditError(saveErrorMessage(e))
      }
    } finally {
      setSavingEdit(false)
    }
  }

  // --- Preferences ---------------------------------------------------------

  const [prefRows, setPrefRows] = React.useState<PrefDraft[]>([])
  const [prefSaving, setPrefSaving] = React.useState(false)
  const [prefError, setPrefError] = React.useState('')
  // Preferences read as chips; the editor opens on demand (design review P5).
  const [editingPrefs, setEditingPrefs] = React.useState(false)

  const updatePrefRow = (key: string, patch: Partial<PrefDraft>) => {
    setPrefRows((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  }

  const removePrefRow = (key: string) => {
    setPrefRows((rows) => rows.filter((r) => r.key !== key))
  }

  const addPrefRow = () => {
    setPrefRows((rows) => [...rows, toDraft({ label: '', value: '', is_health_flag: false })])
  }

  const savePreferences = async () => {
    setPrefSaving(true)
    setPrefError('')
    try {
      const input: PreferenceInput[] = prefRows.map(({ label, value, is_health_flag }) => ({
        label,
        value,
        is_health_flag,
      }))
      const saved = await api.putPreferences(clientId, input)
      setPrefRows(saved.map(toDraft))
      setClient((prev) => (prev ? { ...prev, preferences: saved } : prev))
      setEditingPrefs(false)
      showToast(dispatch, 'Important to know saved')
    } catch (e) {
      setPrefError(e instanceof ValidationError ? validationMessage(e) : saveErrorMessage(e))
    } finally {
      setPrefSaving(false)
    }
  }

  // --- Packages ---------------------------------------------------------

  const [purchases, setPurchases] = React.useState<Purchase[] | null>(null)
  const [purchasesFailed, setPurchasesFailed] = React.useState(false)
  const [sellSheetOpen, setSellSheetOpen] = React.useState(false)
  // The package, and which of its payments, the Refund sheet is open for.
  const [refunding, setRefunding] = React.useState<{ purchase: Purchase; payment: PaymentRecord } | null>(null)
  // Refund is a card action: hidden while card payments are not configured.
  const cardAccount = useCardAccount()
  const showCard = cardPaymentsShown(cardAccount)
  const [removeCardOpen, setRemoveCardOpen] = React.useState(false)
  const purchasesToken = React.useRef(0)

  const loadPurchases = React.useCallback(() => {
    const token = ++purchasesToken.current
    setPurchases(null)
    setPurchasesFailed(false)
    api
      .clientPackages(clientId)
      .then((list) => {
        if (isCurrentRequest(token, purchasesToken.current)) setPurchases(list)
      })
      .catch(() => {
        if (isCurrentRequest(token, purchasesToken.current)) setPurchasesFailed(true)
      })
  }, [clientId])

  React.useEffect(() => {
    loadPurchases()
  }, [loadPurchases])

  const packageSold = (purchase: Purchase, carriedOver = false) => {
    setSellSheetOpen(false)
    setPurchases((prev) => [purchase, ...(prev ?? [])])
    reloadAggregate()
    // A carried-over package is linked to her imported bookings still to come.
    const linked = carriedOver && purchase.linked_bookings ? ` · ${purchase.linked_bookings} booking${purchase.linked_bookings === 1 ? '' : 's'} linked` : ''
    showToast(dispatch, `${purchase.name} ${carriedOver ? 'added' : 'sold'}${linked}`)
  }

  // A paid pay link creates the purchase on the server; reload rather than
  // prepend. The sheet stays up so Jessica sees "Paid" until she closes it.
  const packageCardPaid = () => {
    loadPurchases()
    reloadAggregate()
  }

  const packageRefunded = () => {
    setRefunding(null)
    loadPurchases()
    reloadAggregate()
  }

  // --- Consent ---------------------------------------------------------

  // A grant or withdrawal arrives as a new row, newest first in the history.
  const recordedConsent = (consent: Consent) =>
    setClient((prev) => (prev ? { ...prev, consent: [consent, ...prev.consent] } : prev))

  // --- Timeline ---------------------------------------------------------

  const [timelineEntries, setTimelineEntries] = React.useState<TimelineEntry[] | null>(null)
  const [timelineNextCursor, setTimelineNextCursor] = React.useState<string | null>(null)
  const [timelineFailed, setTimelineFailed] = React.useState(false)
  const [showCancelled, setShowCancelled] = React.useState(false)
  const [loadingMoreTimeline, setLoadingMoreTimeline] = React.useState(false)
  const timelineToken = React.useRef(0)

  const loadTimeline = React.useCallback(() => {
    const token = ++timelineToken.current
    setTimelineEntries(null)
    setTimelineFailed(false)
    api
      .getTimeline(clientId, undefined, showCancelled)
      .then((page) => {
        if (isCurrentRequest(token, timelineToken.current)) {
          setTimelineEntries(page.entries)
          setTimelineNextCursor(page.next_cursor)
        }
      })
      .catch(() => {
        if (isCurrentRequest(token, timelineToken.current)) setTimelineFailed(true)
      })
    // calendarVersion: reload after a change made in the appointment sheet.
  }, [clientId, showCancelled, state.calendarVersion])
  // The header's "£99 to pay": what the visits listed still owe.
  const owedPence = owedOn(timelineEntries)

  React.useEffect(() => {
    loadTimeline()
  }, [loadTimeline])

  const loadMoreTimeline = async () => {
    if (!timelineNextCursor) return
    const token = ++timelineToken.current
    setLoadingMoreTimeline(true)
    try {
      const page = await api.getTimeline(clientId, timelineNextCursor, showCancelled)
      if (isCurrentRequest(token, timelineToken.current)) {
        setTimelineEntries((prev) => [...(prev ?? []), ...page.entries])
        setTimelineNextCursor(page.next_cursor)
      }
    } catch (e) {
      if (isCurrentRequest(token, timelineToken.current)) showToast(dispatch, saveErrorMessage(e))
    } finally {
      setLoadingMoreTimeline(false)
    }
  }

  // --- Their data ----------------------------------------------------

  // Everything held about this client in one file, for answering their
  // request to see it or take it elsewhere.
  const [exporting, setExporting] = React.useState(false)
  const exportData = async () => {
    setExporting(true)
    try {
      await downloadFile(api.exportUrl(`/v1/clients/${encodeURIComponent(clientId)}/export`), 'harmovi-client.json')
    } catch {
      showToast(dispatch, "Couldn’t download. Try again.")
    } finally {
      setExporting(false)
    }
  }

  // --- Erase ---------------------------------------------------------

  const [eraseConfirm, setEraseConfirm] = React.useState(false)
  const [erasing, setErasing] = React.useState(false)
  const [eraseError, setEraseError] = React.useState('')
  const eraseSectionRef = React.useRef<HTMLElement>(null)

  // The confirmation step is a distinct act, not a repeat of the same
  // button: any click outside this section (switching tabs, tapping VIP,
  // opening another sheet) cancels it, so it can never linger armed while
  // the person does something unrelated.
  React.useEffect(() => {
    if (!eraseConfirm) return
    const onDocumentClick = (e: MouseEvent) => {
      if (eraseSectionRef.current && !eraseSectionRef.current.contains(e.target as Node)) {
        setEraseConfirm(false)
      }
    }
    document.addEventListener('click', onDocumentClick, true)
    return () => document.removeEventListener('click', onDocumentClick, true)
  }, [eraseConfirm])

  const startErase = () => {
    setEraseError('')
    setEraseConfirm(true)
  }

  const cancelErase = () => {
    setEraseConfirm(false)
    setEraseError('')
  }

  const confirmErase = async () => {
    setErasing(true)
    setEraseError('')
    try {
      await api.eraseClient(clientId)
      showToast(dispatch, 'Client erased. Their appointment history stays for your accounts.')
      dispatch({ type: 'CLOSE_CLIENT' })
    } catch (e) {
      setEraseError(saveErrorMessage(e))
      setErasing(false)
    }
  }

  // --- Render ---------------------------------------------------------

  if (loadFailed) {
    return (
      <div className="screen client-profile">
        <BackLink onClick={back}>Clients</BackLink>
        <div className="clients-status" role="alert">
          <p>Couldn’t load this client. Check your connection and try again.</p>
          <button type="button" className="btn btn-secondary btn-compact" onClick={loadClient}>
            Try again
          </button>
        </div>
      </div>
    )
  }

  if (!client) {
    return (
      <div className="screen client-profile">
        <BackLink onClick={back}>Clients</BackLink>
        <Loading />
      </div>
    )
  }

  const name = displayName(client)

  return (
    <div className="screen client-profile">
      <BackLink onClick={back}>Clients</BackLink>

      {/* Overview */}
      <section className="profile-section profile-overview">
        <div className="profile-overview__head">
          <h1>{name}</h1>
          <button
            type="button"
            className="chip profile-vip"
            aria-pressed={client.vip}
            disabled={vipSaving}
            onClick={toggleVip}
          >
            {client.vip ? 'VIP' : 'Mark as VIP'}
          </button>
        </div>

        <p className="profile-summary">
          {[
            `${client.visits} ${client.visits === 1 ? 'appointment' : 'appointments'}`,
            `${formatPrice(client.total_spent_pence)} spent`,
            owedPence > 0 ? `${formatPrice(owedPence)} to pay` : null,
            client.next_booking ? `next ${formatDay(client.next_booking)}` : client.last_visit ? `last ${formatDay(client.last_visit)}` : null,
          ].filter(Boolean).join(' · ')}
        </p>
      </section>

      {/* Quick actions */}
      <section className="profile-section profile-actions" aria-label="Quick actions">
        <button type="button" className="btn btn-primary btn-compact" onClick={openBooking}>
          Book
        </button>
        {client.phone && (
          <a className="btn btn-secondary btn-compact" href={telHref(client.phone)}>
            Call
          </a>
        )}
        {client.phone && (
          <a
            className="btn btn-secondary btn-compact"
            href={`https://wa.me/${whatsappNumber(client.phone)}`}
            target="_blank"
            rel="noreferrer"
          >
            WhatsApp
          </a>
        )}
        <button type="button" className="btn btn-secondary btn-compact" onClick={startAddNote}>
          Add note
        </button>
      </section>

      {/* A client the booking page created holds what the person booking typed. */}
      {client.check_details && (
        <section className="profile-section profile-check" aria-label="Check these details">
          {client.possible_duplicate ? (
            <p>This might be the same person as {client.possible_duplicate.name}. Check before you rely on these details.</p>
          ) : (
            <p>Added from your booking page. Check the email and mobile are theirs before you rely on them.</p>
          )}
          <div className="profile-actions">
            <button type="button" className="btn btn-secondary btn-compact" onClick={confirmDetails}>
              Looks right
            </button>
            <button type="button" className="btn btn-quiet btn-compact" onClick={() => { setTab('details'); startEditDetails() }}>
              Edit details
            </button>
          </div>
        </section>
      )}

      {/* Tabs (design review P3): her story first, history and records a tap away. */}
      <div className="profile-tabs" role="tablist" aria-label="About this client">
        {PROFILE_TABS.map((t) => (
          <button key={t.id} type="button" role="tab" id={`profile-tab-${t.id}`} aria-selected={tab === t.id}
            tabIndex={tab === t.id ? 0 : -1}
            aria-controls={`profile-panel-${t.id}`} className="profile-tab" onClick={() => setTab(t.id)}
            onKeyDown={(e) => {
              const next = tabAfterKey(tab, e.key)
              if (next) {
                e.preventDefault()
                setTab(next)
                document.getElementById(`profile-tab-${next}`)?.focus()
              }
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div role="tabpanel" id="profile-panel-overview" aria-labelledby="profile-tab-overview" className="profile-panel">
      {/* Packages */}
      <section className="profile-section">
        <div className="profile-packages__head">
          <h2>Packages</h2>
          <button type="button" className="btn btn-quiet btn-compact" onClick={() => setSellSheetOpen(true)}>
            Sell a package
          </button>
        </div>
        {purchasesFailed ? (
          <div className="clients-status" role="alert">
            <p>Couldn’t load this client’s packages. Check your connection and try again.</p>
            <button type="button" className="btn btn-secondary btn-compact" onClick={loadPurchases}>
              Try again
            </button>
          </div>
        ) : purchases === null ? (
          <Loading />
        ) : purchases.length === 0 ? (
          <p className="clients-status">No packages sold yet.</p>
        ) : (
          <ul className="profile-package-list">
            {purchases.map((purchase) => {
              const status = expiryState(purchase)
              const percent =
                purchase.sessions_total > 0
                  ? Math.round((purchase.remaining / purchase.sessions_total) * 100)
                  : 0
              return (
                <li
                  key={purchase.id}
                  className={`profile-package-row${status === 'expired' ? ' profile-package-row--expired' : ''}`}
                >
                  <div className="profile-package-row__top">
                    <span className="profile-package-row__name">{purchase.name}</span>
                    {status === 'expired' && <span className="tag">Expired</span>}
                    {status === 'expiring' && <span className="tag tag--warning">Expires soon</span>}
                    {disputeTag(purchase) && (
                      <span className={disputeTag(purchase)!.tone === 'danger' ? 'tag tag--danger' : 'tag'}>{disputeTag(purchase)!.text}</span>
                    )}
                  </div>
                  <div className="profile-package-progress">
                    <div className="profile-package-progress__fill" style={{ width: `${percent}%` }} />
                  </div>
                  <div className="profile-package-row__meta">
                    <span>{remainingLabel(purchase)}</span>
                    <span>Bought {asDay(purchase.purchased_at)}</span>
                    {purchase.source === 'import' && <span>Carried over from Fresha</span>}
                    {hasVisibleExpiry(purchase) && <span>Expires {asDay(purchase.expires_at as string)}</span>}
                    {purchase.paid_by_card && <span>Paid by card</span>}
                    {purchase.refunded_pence > 0 && <span>{formatPrice(purchase.refunded_pence)} refunded</span>}
                  </div>
                  {purchase.refunded_outside && (
                    <p className="field-hint">Refunded outside Harmovi — check this package’s sessions</p>
                  )}
                  <PaymentHistory
                    payments={purchase.payments}
                    canRefund={(p) => p.refundable_pence > 0 && (!p.through_stripe || showCard)}
                    onRefund={(payment) => setRefunding({ purchase, payment })}
                  />
                </li>
              )
            })}
          </ul>
        )}
      </section>

      {/* Notes */}
      <section className="profile-section" ref={notesSectionRef}>
        <h2>Notes</h2>
        {addingNote && (
          <div className="profile-note-compose">
            <textarea
              ref={newNoteRef}
              rows={3}
              value={newNoteText}
              onChange={(e) => setNewNoteText(e.target.value)}
              placeholder="What happened at this appointment."
            />
            {newNoteError && <p className="field-error" role="alert">{newNoteError}</p>}
            <div className="profile-note-compose__actions">
              <button type="button" className="btn btn-secondary btn-compact" onClick={() => setAddingNote(false)} disabled={savingNewNote}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={saveNewNote} disabled={savingNewNote}>
                {savingNewNote ? 'Saving…' : 'Save note'}
              </button>
            </div>
          </div>
        )}
        {notesFailed ? (
          <div className="clients-status" role="alert">
            <p>Couldn’t load this client’s notes. Check your connection and try again.</p>
            <button type="button" className="btn btn-secondary btn-compact" onClick={loadNotes}>
              Try again
            </button>
          </div>
        ) : notes === null ? (
          <Loading />
        ) : notes.length === 0 ? (
          !addingNote && <p className="clients-status">No notes yet.</p>
        ) : (
          <ul className="profile-note-list">
            {notes.map((note) => (
              <li key={note.id} className="profile-note-row">
                {editingNoteId === note.id ? (
                  <>
                    <textarea rows={3} value={editText} onChange={(e) => setEditText(e.target.value)} />
                    {editError && <p className="field-error" role="alert">{editError}</p>}
                    <div className="profile-note-compose__actions">
                      <button
                        type="button"
                        className="btn btn-secondary btn-compact"
                        onClick={() => setEditingNoteId(null)}
                        disabled={savingEdit}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => saveEditNote(note)}
                        disabled={savingEdit}
                      >
                        {savingEdit ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="profile-note-row__text">{note.text}</p>
                    {note.appointment_id && note.photos && note.photos.length > 0 && (
                      <NotePhotoGrid
                        clientId={clientId}
                        noteId={note.id}
                        photos={note.photos}
                        onRemoved={(photoId) => photoRemoved(note.id, photoId)}
                      />
                    )}
                    <div className="profile-note-row__meta">
                      <span>{note.appointment_id ? `Appointment notes · ${asDay(note.updated_at)}` : asDay(note.updated_at)}</span>
                      <button type="button" className="btn btn-quiet btn-compact" onClick={() => startEditNote(note)}>
                        Edit
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Preferences */}
      <section className="profile-section">
        <div className="profile-section__head">
          <h2>Important to know</h2>
          {!editingPrefs && (
            <button type="button" className="btn btn-quiet btn-compact" onClick={() => setEditingPrefs(true)}>
              {client.preferences.length === 0 ? 'Add' : 'Edit'}
            </button>
          )}
        </div>
        {!editingPrefs ? (
          client.preferences.length === 0 ? (
            <p className="clients-status">Nothing noted yet.</p>
          ) : (
            <ul className="profile-pref-chips">
              {importantFirst(client.preferences).map((p) => (
                <li key={`${p.label}-${p.value}`} className={p.is_health_flag ? 'profile-pref-chip profile-pref-chip--health' : 'profile-pref-chip'}>
                  {p.is_health_flag && <span className="profile-pref-chip__health">Health · </span>}<span className="profile-pref-chip__label">{p.label}</span> {p.value}
                </li>
              ))}
            </ul>
          )
        ) : (
        <>
        {prefRows.length === 0 && <p className="clients-status">Nothing noted yet.</p>}
        <ul className="profile-pref-list">
          {prefRows.map((row, index) => (
            <li key={row.key} className="profile-pref-row">
              <div className="field">
                <label htmlFor={`pref-${row.key}-label`}>What</label>
                <input
                  id={`pref-${row.key}-label`}
                  placeholder="e.g. Allergy"
                  value={row.label}
                  onChange={(e) => updatePrefRow(row.key, { label: e.target.value })}
                />
              </div>
              <div className="field">
                <label htmlFor={`pref-${row.key}-value`}>Details</label>
                <textarea
                  id={`pref-${row.key}-value`}
                  rows={2}
                  placeholder="e.g. Nuts, including almond oil"
                  value={row.value}
                  onChange={(e) => updatePrefRow(row.key, { value: e.target.value })}
                />
              </div>
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={row.is_health_flag}
                  onChange={(e) => updatePrefRow(row.key, { is_health_flag: e.target.checked })}
                />
                Health or allergy (always shown first)
              </label>
              <button
                type="button"
                className="btn btn-quiet btn-compact profile-pref-remove"
                aria-label={`Remove detail ${index + 1}${row.label ? `, ${row.label}` : ''}`}
                onClick={() => removePrefRow(row.key)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
        <div className="profile-pref-actions">
          <button type="button" className="btn btn-quiet btn-compact" onClick={addPrefRow}>
            Add a detail
          </button>
          <button type="button" className="btn btn-primary" onClick={savePreferences} disabled={prefSaving}>
            {prefSaving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="btn btn-quiet btn-compact" disabled={prefSaving}
            onClick={() => { setPrefRows(client.preferences.map(toDraft)); setPrefError(''); setEditingPrefs(false) }}>
            Cancel
          </button>
        </div>
        </>
        )}
        {prefError && <p className="field-error" role="alert">{prefError}</p>}
      </section>

        </div>
      )}

      {tab === 'history' && (
        <div role="tabpanel" id="profile-panel-history" aria-labelledby="profile-tab-history" className="profile-panel">
      {/* Timeline */}
      <section className="profile-section">
        <div className="profile-timeline__head">
          <h2>Timeline</h2>
          <label className="checkbox-row">
            <input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} />
            Show cancelled
          </label>
        </div>
        {timelineFailed ? (
          <div className="clients-status" role="alert">
            <p>Couldn’t load this client’s timeline. Check your connection and try again.</p>
            <button type="button" className="btn btn-secondary btn-compact" onClick={loadTimeline}>
              Try again
            </button>
          </div>
        ) : timelineEntries === null ? (
          <Loading />
        ) : timelineEntries.length === 0 ? (
          <p className="clients-status">No appointments yet.</p>
        ) : (
          <>
            <ul className="profile-timeline-list">
              {timelineEntries.map((entry) => (
                <li key={entry.appointment_id}>
                  {/* Opens the appointment's sheet over her profile (feedback 2026-10-11). */}
                  <button
                    type="button"
                    className="profile-timeline-row profile-timeline-row--button"
                    onClick={() => dispatch({ type: 'OPEN_APPOINTMENT', payload: entry.appointment_id })}
                  >
                    <span className="profile-timeline-row__top">
                      <span className="profile-timeline-row__date">{formatDay(entry.date)}</span>
                      <span className={entry.status === 'cancelled' || entry.status === 'no_show' ? 'tag tag--danger' : 'tag'}>{STATUS_LABELS[entry.status]}</span>
                    </span>
                    <span className="profile-timeline-row__service">
                      {entry.service_name} · {formatLength(entry.duration_minutes)} · <VisitPrice pence={entry.price_pence} paymentState={entry.payment_state} />
                    </span>
                    {paymentStateLabel(entry.payment_state, entry.paid_method, entry.amount_due_pence) && (
                      <span className={`profile-timeline-row__payment${entry.payment_state === 'to_pay' ? ' profile-timeline-row__payment--due' : ''}`}>
                        {paymentStateLabel(entry.payment_state, entry.paid_method, entry.amount_due_pence)}
                      </span>
                    )}
                    {entry.note_text && <span className="profile-timeline-row__note">{entry.note_text}</span>}
                  </button>
                </li>
              ))}
            </ul>
            {timelineNextCursor && (
              <button type="button" className="btn btn-quiet btn-block" onClick={loadMoreTimeline} disabled={loadingMoreTimeline}>
                {loadingMoreTimeline ? 'Loading…' : 'Load more'}
              </button>
            )}
          </>
        )}
      </section>

        </div>
      )}

      {tab === 'details' && (
        <div role="tabpanel" id="profile-panel-details" aria-labelledby="profile-tab-details" className="profile-panel">
      <section className="profile-section">
        <div className="profile-timeline__head">
          <h2>Contact and appointments</h2>
          {!editingDetails && (
            <button type="button" className="btn btn-quiet btn-compact" onClick={startEditDetails}>
              Edit details
            </button>
          )}
        </div>
        {editingDetails && (
          <div className="settings-form profile-details-form">
            {client.email_confirmed ? (
              <div className="field">
                <span className="field-label">Email</span>
                <p>{client.email}</p>
                <p className="field-hint">Confirmed by {client.first_name}. To change it, email hello@harmovi.com.</p>
              </div>
            ) : (
              <div className="field">
                <label htmlFor="client-email">Email</label>
                <input id="client-email" type="email" inputMode="email" autoComplete="off" value={emailDraft}
                  onChange={(e) => setEmailDraft(e.target.value)} />
                {detailErrors.email ? <p className="field-error" role="alert">{detailErrors.email}</p>
                  : <p className="field-hint">For appointment emails and deposit links.</p>}
              </div>
            )}
            <fieldset className="field birthday-fields">
              <legend>Birthday</legend>
              <div className="birthday-fields__row">
                <input aria-label="Day" inputMode="numeric" placeholder="Day" value={birthdayDraft.day}
                  onChange={(e) => setBirthdayDraft({ ...birthdayDraft, day: e.target.value })} />
                <select aria-label="Month" value={birthdayDraft.month}
                  onChange={(e) => setBirthdayDraft({ ...birthdayDraft, month: e.target.value })}>
                  <option value="">Month</option>
                  {BIRTHDAY_MONTHS.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
                </select>
                <input aria-label="Year (optional)" inputMode="numeric" placeholder="Year" value={birthdayDraft.year}
                  onChange={(e) => setBirthdayDraft({ ...birthdayDraft, year: e.target.value })} />
              </div>
              {detailErrors.birthday ? <p className="field-error" role="alert">{detailErrors.birthday}</p>
                : <p className="field-hint">The year is optional. Birthdays show on Today in the week before.</p>}
            </fieldset>
            <div className="profile-note-compose__actions">
              <button type="button" className="btn btn-secondary btn-compact" onClick={() => setEditingDetails(false)} disabled={detailsSaving}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={saveDetails} disabled={detailsSaving}>
                {detailsSaving ? 'Saving…' : 'Save details'}
              </button>
            </div>
          </div>
        )}
          <dl className="profile-facts">
          {client.phone && (
            <div className="profile-fact">
              <dt>Phone</dt>
              <dd>{displayPhone(client.phone)}</dd>
            </div>
          )}
          {client.email && (
            <div className="profile-fact">
              <dt>Email</dt>
              <dd>
                {client.email}{' '}
                <span className={client.email_confirmed ? 'tag tag--positive' : 'tag'}>
                  {client.email_confirmed ? 'Confirmed' : 'Not confirmed yet'}
                </span>
                {!client.email_confirmed && (
                  <>
                    {' '}
                    <button type="button" className="btn btn-quiet btn-compact" disabled={confirmationSending} onClick={sendConfirmation}>
                      Send confirmation
                    </button>
                  </>
                )}
                {confirmationNote && <span className="field-hint" role="status"> {confirmationNote}</span>}
                {confirmationError && <span className="field-error" role="alert"> {confirmationError}</span>}
              </dd>
            </div>
          )}
          {client.birthday && formatBirthday(client.birthday) && (
            <div className="profile-fact">
              <dt>Birthday</dt>
              <dd>{formatBirthday(client.birthday)}</dd>
            </div>
          )}
          <div className="profile-fact">
            <dt>Total spent</dt>
            <dd>{formatPrice(client.total_spent_pence)}</dd>
          </div>
          <div className="profile-fact">
            <dt>Appointments</dt>
            <dd>{client.visits}</dd>
          </div>
          {client.last_visit && (
            <div className="profile-fact">
              <dt>Last appointment</dt>
              <dd>{formatDay(client.last_visit)}</dd>
            </div>
          )}
          {client.next_booking && (
            <div className="profile-fact">
              <dt>Next appointment</dt>
              <dd>{formatDay(client.next_booking)}</dd>
            </div>
          )}
        </dl>
      </section>
      {/* Card on file: only while card payments are ready */}
      {canTakeCardPayments(cardAccount) && (
        <section className="profile-section">
          <h2>Card on file</h2>
          {client.card ? (
            <div className="profile-card">
              <div className="profile-card__main">
                <span className="profile-card__label">{cardLabel(client.card)}</span>
                <span className="profile-card__expiry">
                  Expires {cardExpiry(client.card)}{' '}
                  {client.card.expired && <span className="tag tag--warning">Expired</span>}
                </span>
              </div>
              <button type="button" className="btn btn-danger btn-compact" onClick={() => setRemoveCardOpen(true)}>
                Remove
              </button>
            </div>
          ) : (
            <p className="field-hint">No card saved. Clients can save one when they pay a deposit online or pay a link you send.</p>
          )}
        </section>
      )}
      {/* Consent */}
      <section className="profile-section">
        <h2>Consent</h2>
        <ConsentRow
          kind="records"
          label="Treatment records"
          client={client}
          businessName={businessName}
          onRecorded={recordedConsent}
          emptyHint="Not recorded. Signing a consultation form records it."
        />
        <ConsentRow
          kind="photos"
          label="Treatment photos"
          client={client}
          businessName={businessName}
          onRecorded={recordedConsent}
          emptyHint="Not recorded."
        />
      </section>

      <ClientForms
        client={client}
        onClientChange={setClient}
        onPreferencesChange={(preferences) => applyClient({ ...client, preferences })}
      />

      <ClientReviews client={client} onClientChange={setClient} />

      {/* Erasure */}
      <section className="profile-section profile-erase" ref={eraseSectionRef}>
        {eraseConfirm ? (
          <div className="profile-erase-confirm">
            <p>
              This permanently deletes {name}’s notes, Important to know details and consent records, and the words of any review
              they left. Their appointments and payment history stay.
            </p>
            {eraseError && <p className="field-error" role="alert">{eraseError}</p>}
            <div className="profile-erase-confirm__actions">
              <button type="button" className="btn btn-secondary btn-compact" onClick={cancelErase} disabled={erasing}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger btn-compact" onClick={confirmErase} disabled={erasing}>
                {erasing ? 'Erasing…' : 'Erase permanently'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <button type="button" className="btn btn-secondary btn-compact" onClick={exportData} disabled={exporting}>
              {exporting ? 'Downloading…' : 'Download their data'}
            </button>
            <p className="field-hint">Everything you hold about {name}, for when they ask to see it.</p>
            <button type="button" className="btn btn-danger btn-compact" onClick={startErase}>
              Erase client
            </button>
            <p className="field-hint">Removes personal and health information. Financial records stay.</p>
          </>
        )}
      </section>

        </div>
      )}

      {refunding && (
        <RefundSheet
          paymentId={refunding.payment.id}
          maxPence={refunding.payment.refundable_pence}
          recordedMethod={refunding.payment.through_stripe ? undefined : refunding.payment.method}
          toCard={refundsToCard(refunding.payment)}
          packageCredits={{
            available: refunding.purchase.remaining,
            pricePaidPence: refunding.purchase.price_paid_pence,
            sessionsTotal: refunding.purchase.sessions_total,
          }}
          onClose={() => setRefunding(null)}
          onDone={packageRefunded}
        />
      )}
      {removeCardOpen && (
        <RemoveCardSheet
          clientId={clientId}
          firstName={client.first_name}
          onClose={() => setRemoveCardOpen(false)}
          onRemoved={() => {
            setRemoveCardOpen(false)
            setClient({ ...client, card: null })
            showToast(dispatch, 'Card removed')
          }}
        />
      )}
      {sellSheetOpen && (
        <SellPackageSheet
          clientId={clientId}
          clientName={name}
          card={canTakeCardPayments(cardAccount) ? client.card : null}
          clientHasEmail={!!client.email}
          carryOverUntil={state.me?.fresha_carry_over_until ?? null}
          onClose={() => setSellSheetOpen(false)}
          onSold={packageSold}
          onCardPaid={packageCardPaid}
        />
      )}
    </div>
  )
}
